"use client";

import { useState, useEffect, useMemo } from "react";
import Image from "next/image";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Typography } from "@/components/typography/typography";
import { Select } from "@/old-app/store/components/select";
import { useMediaQuery } from "@/hook/use-media-query";
import { productService } from "@/services/product";
import { useDebounce } from "@/hook/use-debounce";
import { priceSymbol } from "@/utils/price-count";
import { categoryService } from "@/services/category";
import { CustomRadio } from "./CustomRadio";
import { Modal } from "../modal";
import BreadCrumb from "../breadcrumb/breadcrumb";
import DismissibleChip from "../dismissible-chip/dismissible-chip";
import { InfiniteLoader } from "../infinite-loader";
import CheckboxList from "./CustomCheckbox";
import { ProductCardUI } from "../product-card/product-card-ui";

interface SortOption {
  id: number;
  title: string;
  sortBy?: string;
  sortOrder?: string;
}

interface QueryParams {
  categorySlugs?: string | string[];
  minPrice?: number | undefined;
  maxPrice?: number | undefined;
  sortBy?: string;
  sortOrder?: string;
  minDiscount?: number | undefined;
  maxDiscount?: number | undefined;
  discount?: boolean;
  name?: string;
}

const priceRange = [
  { id: 1, title: "All Price" },
  { id: 2, title: `Under ${priceSymbol(20)}`, minPrice: 1, maxPrice: 20 },
  {
    id: 3,
    title: `${priceSymbol(20)} to ${priceSymbol(100)}`,
    minPrice: 20,
    maxPrice: 100,
  },
  {
    id: 4,
    title: `${priceSymbol(100)} to ${priceSymbol(300)}`,
    minPrice: 100,
    maxPrice: 300,
  },
  {
    id: 5,
    title: `${priceSymbol(300)} to ${priceSymbol(500)}`,
    minPrice: 300,
    maxPrice: 500,
  },
  {
    id: 6,
    title: `${priceSymbol(500)} to ${priceSymbol("1,000")}`,
    minPrice: 500,
    maxPrice: 1000,
  },
  {
    id: 7,
    title: `${priceSymbol("1,000")} to ${priceSymbol("10,000")}`,
    minPrice: 1000,
    maxPrice: 10000,
  },
];

const discountRange = [
  { id: 1, title: "All Discounts", discount: true },
  {
    id: 2,
    title: `Under ${priceSymbol(20)}`,
    discount: undefined,
    minDiscount: 1,
    maxDiscount: 20,
  },
  {
    id: 3,
    title: `${priceSymbol(20)} to ${priceSymbol(100)}`,
    discount: undefined,
    minDiscount: 20,
    maxDiscount: 100,
  },
  {
    id: 4,
    title: `${priceSymbol(100)} to ${priceSymbol(300)}`,
    discount: undefined,
    minDiscount: 100,
    maxDiscount: 300,
  },
  {
    id: 5,
    title: `${priceSymbol(300)} to ${priceSymbol(500)}`,
    discount: undefined,
    minDiscount: 300,
    maxDiscount: 500,
  },
];

const sortOptions: SortOption[] = [
  { id: 1, title: "Newest Arrivals" },
  { id: 2, title: "Price: Low to High", sortBy: "price", sortOrder: "asc" },
  { id: 3, title: "Price: High to Low", sortBy: "price", sortOrder: "desc" },
  { id: 4, title: "Name: A to Z", sortBy: "name", sortOrder: "asc" },
  { id: 5, title: "Name: Z to A", sortBy: "name", sortOrder: "desc" },
];

export const Products = () => {
  const searchParams =
    typeof window !== "undefined" ? window.location.search : undefined;

  const [selectedPrice, setSelectedPrice] = useState<string>(
    priceRange[0].title,
  );
  const [selectedDiscount, setSelectedDiscount] = useState<string | undefined>(
    undefined,
  );
  const [minDiscount, setMinDiscount] = useState<number | undefined>(undefined);
  const [maxDiscount, setMaxDiscount] = useState<number | undefined>(undefined);
  const [minPrice, setMinPrice] = useState<number | undefined>(undefined);
  const [maxPrice, setMaxPrice] = useState<number | undefined>(undefined);
  const [selectedCategory, setSelectedCategory] = useState<string[]>([]);
  const [selectedSortOption, setSelectedSortOption] = useState<SortOption>(
    sortOptions[0],
  );
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const isXl = useMediaQuery("(min-width: 1280px)");

  useEffect(() => {
    window.scroll(0, 0);
  }, [
    selectedCategory,
    selectedPrice,
    selectedDiscount,
    selectedSortOption,
    searchQuery,
  ]);

  const {
    data: categories,
    fetchNextPage: fetchNextCategories,
    hasNextPage: hasNextCategories,
    isFetchingNextPage: isFetchingNextCategories,
  } = useInfiniteQuery(
    ["categories"],
    ({ pageParam = 1 }) =>
      categoryService.getAllCategories({ page: pageParam, limit: 8 }),
    {
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.data.total > allPages.length * 8) {
          return allPages.length + 1;
        }
        return undefined;
      },
    },
  );

  const allCategories = useMemo(
    () =>
      categories?.pages
        .map((category) =>
          category.data.data.map(({ name, slug }) => ({ name, slug })),
        )
        .filter(Boolean)
        .flat(1) || [],
    [categories],
  );

  useEffect(() => {
    const filtersApplied =
      selectedCategory.length > 0 ||
      selectedPrice !== priceRange[0].title ||
      selectedDiscount !== undefined;
    setIsFilterApplied(filtersApplied);
  }, [selectedCategory, selectedPrice, selectedDiscount]);

  useEffect(() => {
    const parseCategorySlugs = (params: URLSearchParams) => {
      const categorySlugsParam = params.get("categorySlugs");
      if (categorySlugsParam) {
        const parsedCategorySlugs = Array.isArray(categorySlugsParam)
          ? categorySlugsParam
          : JSON.parse(categorySlugsParam);
        setSelectedCategory(parsedCategorySlugs);
      }
    };

    const parsePriceParams = (params: URLSearchParams) => {
      const minPriceParam = params.get("minPrice");
      const maxPriceParam = params.get("maxPrice");
      if (minPriceParam !== null) {
        setMinPrice(Number(minPriceParam));
      }
      if (maxPriceParam !== null) {
        setMaxPrice(Number(maxPriceParam));
      }
    };

    const updateSelectedPrice = (params: URLSearchParams) => {
      const minPriceParam = params.get("minPrice");
      const maxPriceParam = params.get("maxPrice");
      if (minPriceParam !== null || maxPriceParam !== null) {
        const selectedRange = priceRange.find(
          (price) =>
            price.minPrice === Number(minPriceParam) &&
            price.maxPrice === Number(maxPriceParam),
        );
        if (selectedRange) {
          setSelectedPrice(selectedRange.title);
        }
      } else {
        setSelectedPrice(priceRange[0].title);
      }
    };

    const updateSelectedSortOption = (params: URLSearchParams) => {
      const sortByParam = params.get("sortBy");
      const sortOrderParam = params.get("sortOrder");
      if (sortByParam !== null && sortOrderParam !== null) {
        const selectedOption = sortOptions.find(
          (option) =>
            option.sortBy === sortByParam &&
            option.sortOrder === sortOrderParam,
        );
        if (selectedOption) {
          setSelectedSortOption(selectedOption);
        }
      } else {
        setSelectedSortOption(sortOptions[0]);
      }
    };

    const parseDiscountParams = (params: URLSearchParams) => {
      const minDiscountParam = params.get("minDiscount");
      const maxDiscountParam = params.get("maxDiscount");
      const discountParam = params.get("discount");

      if (minDiscountParam !== null || maxDiscountParam !== null) {
        const selectedRange = discountRange.find(
          (discount) =>
            discount.minDiscount === Number(minDiscountParam) &&
            discount.maxDiscount === Number(maxDiscountParam),
        );
        if (selectedRange) {
          setSelectedDiscount(selectedRange.title);
          setMinDiscount(selectedRange.minDiscount);
          setMaxDiscount(selectedRange.maxDiscount);
        }
      } else if (discountParam !== null) {
        setSelectedDiscount(discountRange[0].title);
      } else {
        setSelectedDiscount(undefined);
        setMinDiscount(undefined);
        setMaxDiscount(undefined);
      }
    };

    const handleURLChange = () => {
      const params = new URLSearchParams(window.location.search);
      parseCategorySlugs(params);
      parsePriceParams(params);
      updateSelectedPrice(params);
      updateSelectedSortOption(params);
      parseDiscountParams(params);
    };

    handleURLChange();
  }, [searchParams, allCategories]);

  const updateQueryParams = (newParams: Partial<QueryParams>) => {
    const currentParams = new URLSearchParams(window.location.search);
    Object.entries(newParams).forEach(([key, value]) => {
      if (value !== undefined) {
        currentParams.set(key, String(value));
      } else {
        currentParams.delete(key);
      }
    });

    const newQueryString = currentParams.toString();
    const newUrl = `${window.location.pathname}?${newQueryString}`;
    window.history.pushState({}, "", newUrl);
  };

  const handleCategoryChange = (newSelectedCategories: string[]) => {
    const selectedSlugs = allCategories
      .filter((category) => newSelectedCategories.includes(category.name))
      .map((category) => category.slug);

    setSelectedCategory(selectedSlugs);
    updateQueryParams({ categorySlugs: JSON.stringify(selectedSlugs) });
  };

  const handleRemoveFilter = (categorySlug: string) => {
    const updatedCategories = selectedCategory.filter(
      (c) => c !== categorySlug,
    );
    setSelectedCategory(updatedCategories);
    updateQueryParams({ categorySlugs: JSON.stringify(updatedCategories) });
  };

  const handleRemovePriceFilter = () => {
    setSelectedPrice(priceRange[0].title);
    setMinPrice(undefined);
    setMaxPrice(undefined);
    updateQueryParams({ minPrice: undefined, maxPrice: undefined });
  };

  const handleRemoveDiscountFilter = () => {
    setSelectedDiscount(undefined);
    setMinDiscount(undefined);
    setMaxDiscount(undefined);
    updateQueryParams({
      discount: undefined,
      minDiscount: undefined,
      maxDiscount: undefined,
    });
  };

  const getCategoryNameFromSlug = (slug: string) => {
    const categoryName = allCategories.find(
      (category) => category.slug === slug,
    );
    return categoryName ? categoryName.name : slug;
  };

  if (isXl && isFilterModalOpen) {
    setIsFilterModalOpen(false);
  }

  const handleSelectSortOption = (option: SortOption) => {
    setSelectedSortOption(option);
    updateQueryParams({
      sortBy: option.sortBy ?? undefined,
      sortOrder: option.sortOrder ?? undefined,
    });
  };

  const handlePriceChange = (price: {
    id: number;
    title: string;
    minPrice?: number;
    maxPrice?: number;
  }) => {
    setSelectedPrice(price.title);
    setMinPrice(price.minPrice);
    setMaxPrice(price.maxPrice);
    updateQueryParams({
      minPrice: price.minPrice ?? undefined,
      maxPrice: price.maxPrice ?? undefined,
    });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value?.trimStart());
  };

  const handleSearchSubmit = () => {
    updateQueryParams({ name: searchQuery.trimStart() || undefined });
  };

  const clearSearchQuery = () => {
    setSearchQuery("");
    updateQueryParams({ name: undefined });
  };

  const handleDiscountChange = (discount: {
    id: number;
    title: string;
    minDiscount?: number;
    maxDiscount?: number;
    discount?: boolean;
  }) => {
    setSelectedDiscount(discount.title);
    setMinDiscount(discount.minDiscount);
    setMaxDiscount(discount.maxDiscount);
    updateQueryParams({
      discount: discount.discount ?? undefined,
      minDiscount: discount.minDiscount ?? undefined,
      maxDiscount: discount.maxDiscount ?? undefined,
    });
  };

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const {
    data: products,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    [
      "products-filter",
      selectedCategory,
      minPrice,
      maxPrice,
      debouncedSearchQuery,
      selectedSortOption,
      selectedDiscount,
      minDiscount,
      maxDiscount,
    ],
    ({ pageParam = 1 }) =>
      productService.getAllProducts({
        page: pageParam,
        limit: 8,
        categorySlugs: JSON.stringify(selectedCategory) || undefined,
        minPrice,
        maxPrice,
        name: debouncedSearchQuery?.trim() || undefined,
        sortBy: selectedSortOption.sortBy ?? undefined,
        sortOrder: selectedSortOption.sortOrder ?? undefined,
        discount:
          selectedDiscount === discountRange[0].title ? true : undefined,
        minDiscount,
        maxDiscount,
      }),
    {
      suspense: true,
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.data.total > allPages.length * 8) {
          return allPages.length + 1;
        }
        return undefined;
      },
    },
  );

  const extractKey = (option: SortOption) => option.id;
  const extractTitle = (option?: SortOption) => option?.title;

  const filterPanel = (
    <div className="flex flex-col justify-start max-w-[270px] w-full p-4 sm:p-8 xl:p-4 xl:pt-0">
      <div className="flex flex-col">
        <Typography
          tag="h2"
          className="xl:text-lg font-semibold text-zinc-700 uppercase"
        >
          Category
        </Typography>
        <div className="flex flex-col gap-1 mt-2">
          <InfiniteLoader
            loadMore={fetchNextCategories}
            hasMore={hasNextCategories}
            loading={isFetchingNextCategories}
            manualLoad
          >
            <CheckboxList
              items={allCategories.map(({ name }) => name)}
              selectedItems={allCategories
                .filter((category) => selectedCategory.includes(category.slug))
                .map((category) => category.name)}
              onChange={handleCategoryChange}
            />
          </InfiniteLoader>
        </div>
      </div>

      <hr className="my-6" />
      <div className="flex flex-col">
        <Typography
          tag="h2"
          className="xl:text-lg font-semibold text-zinc-800 uppercase"
        >
          Price Range
        </Typography>
        <div className="flex flex-col mt-4 gap-1">
          {priceRange.map((price) => (
            <CustomRadio
              key={price.id}
              name="price"
              value={price.title}
              label={price.title}
              checked={selectedPrice === price.title}
              onChange={() => handlePriceChange(price)}
            />
          ))}
        </div>
      </div>
      <hr className="my-6" />
      <div className="flex flex-col">
        <Typography
          tag="h2"
          className="xl:text-lg font-semibold text-zinc-800 uppercase"
        >
          Discount Range
        </Typography>
        <div className="flex flex-col mt-4 gap-1">
          {discountRange.map((discount) => (
            <CustomRadio
              key={discount.id}
              name="discount"
              value={discount.title}
              label={discount.title}
              checked={selectedDiscount === discount.title}
              onChange={() => handleDiscountChange(discount)}
            />
          ))}
        </div>
      </div>
    </div>
  );

  const handleClearAllFilters = () => {
    setSelectedCategory([]);
    setSelectedPrice(priceRange[0].title);
    setMinPrice(undefined);
    setMaxPrice(undefined);
    setSelectedDiscount(undefined);
    setMinDiscount(undefined);
    setMaxDiscount(undefined);
    updateQueryParams({
      categorySlugs: undefined,
      minPrice: undefined,
      maxPrice: undefined,
      discount: undefined,
      minDiscount: undefined,
      maxDiscount: undefined,
      sortBy: undefined,
      sortOrder: undefined,
      name: undefined,
    });
    setSearchQuery("");
  };

  return (
    <section className="bg-white">
      <div className="bg-gray-100 px-2 md:px-4">
        <BreadCrumb
          routes={[
            { name: "Home", href: "/" },
            { name: "Products", href: "/products" },
          ]}
        />
      </div>
      <div className="container flex my-6 mx-auto gap-6">
        <div className="hidden xl:flex flex-col justify-start max-w-[270px] w-full h-screen sticky top-52">
          <div
            className="flex flex-col overflow-y-auto"
            style={{ height: "calc(100vh - 270px)" }}
          >
            {filterPanel}
          </div>
        </div>
        <div className="flex flex-col w-full">
          <div className="flex flex-col md:flex-row justify-between items-center px-2 gap-4">
            <div className="relative mx-auto md:mx-0 border-2 border-gray-inputBorder rounded w-full md:max-w-sm xl:max-w-lg">
              <input
                type="text"
                placeholder="Search for anything..."
                className="transition-all flex py-3 px-4 text-base border pr-10 rounded w-full focus-visible:ring-primary focus-visible:ring-2 focus:outline-none"
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={(e) => e.key === "Enter" && handleSearchSubmit()}
              />
              {searchQuery ? (
                <button
                  className="absolute top-1/2 right-4 transform -translate-y-1/2 w-5 h-5"
                  onClick={clearSearchQuery}
                >
                  <Image
                    src="/img/cross.svg"
                    alt="Clear"
                    className="mr-2"
                    height={22}
                    width={22}
                  />
                </button>
              ) : (
                <button
                  className="absolute top-1/2 right-4 transform -translate-y-1/2 w-5 h-5"
                  onClick={handleSearchSubmit}
                >
                  <Image
                    src="/img/search.svg"
                    alt="Search"
                    className="mr-2"
                    height={22}
                    width={22}
                  />
                </button>
              )}
            </div>
            <div className="flex justify-between gap-6">
              <div className="flex justify-between items-center gap-4">
                <Typography tag="p" className="text-nowrap">
                  Sort by:
                </Typography>
                <Select
                  options={sortOptions}
                  extractKey={extractKey}
                  extractTitle={extractTitle}
                  onSelect={handleSelectSortOption}
                  value={selectedSortOption}
                />
              </div>
              <div className="xl:hidden flex shrink-0">
                <button onClick={() => setIsFilterModalOpen(true)}>
                  <Image
                    src="/img/filter.svg"
                    alt="filters"
                    height={25}
                    width={32}
                  />
                </button>
              </div>
            </div>
          </div>
          <div className="flex flex-col md:flex-row justify-between md:items-center bg-gray-bg1 m-3 md:mx-2 px-6 py-2">
            <div className="flex gap-1 sm:gap-5 items-center flex-wrap ">
              <Typography
                tag="p"
                className=" bg-gray-bg1 text-gray-500 text-nowrap"
              >
                Active Filters:
              </Typography>
              <div className="flex bg-gray-bg1 items-center flex-wrap gap-2 md:max-w-md lg:max-w-2xl xl:max-w-xl 2xl:max-w-4xl py-2">
                {selectedCategory.map((category) => (
                  <DismissibleChip
                    key={category}
                    label={getCategoryNameFromSlug(category)}
                    onRemove={() => handleRemoveFilter(category)}
                  />
                ))}
                {selectedPrice !== priceRange[0].title && (
                  <DismissibleChip
                    key="price"
                    label={selectedPrice}
                    onRemove={handleRemovePriceFilter}
                  />
                )}
                {selectedDiscount && (
                  <DismissibleChip
                    key="discount"
                    label={selectedDiscount}
                    onRemove={handleRemoveDiscountFilter}
                  />
                )}
              </div>
            </div>
            <div className="flex md:flex-col items-center justify-between">
              <div className="flex gap-2 items-center py-2">
                <div className="font-semibold">
                  {products?.pages[0]?.data?.total}
                </div>
                <Typography tag="p" className="my-1 font-medium text-gray-400">
                  Results found.
                </Typography>
              </div>
              {isFilterApplied && (
                <div>
                  <button
                    onClick={handleClearAllFilters}
                    className="transition-all bg-transparent text-primary border-2 border-primary py-1 px-3 mb-2 rounded-lg font-semibold text-sm hover:text-white hover:bg-primary hover:scale-105 active:scale-100"
                  >
                    Clear All &nbsp; &#x2715;
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col">
            <InfiniteLoader
              loadMore={fetchNextPage}
              hasMore={hasNextPage}
              loading={isFetchingNextPage}
            >
              {products?.pages?.[0]?.data?.data?.length === 0 ? (
                <div className="flex flex-col items-center justify-center my-36">
                  <Image
                    src="/img/no-products-found.webp"
                    alt="No products found"
                    width={260}
                    height={300}
                  />
                  <Typography
                    tag="h2"
                    className="font-medium text-3xl text-primary my-8 mr-4"
                  >
                    No Products found
                  </Typography>
                </div>
              ) : (
                <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 p-3">
                  {products?.pages?.map((p) =>
                    p.data.data.length
                      ? p.data.data.map((product) => (
                          <ProductCardUI
                            key={product.id}
                            product={product}
                            pageType="products"
                          />
                        ))
                      : null,
                  )}
                </div>
              )}
            </InfiniteLoader>
          </div>
        </div>
      </div>
      <Modal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        withCloseButton
        size="medium"
      >
        {filterPanel}
      </Modal>
    </section>
  );
};

export default Products;
